import {
  closestCorners,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { SkeletonWrapper } from '@shared/components'
import { useQueryParams } from '@shared/hooks'
import { showApiErrors } from '@shared/utils'
import {
  QueryKey,
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQueryClient
} from '@tanstack/react-query'
import { App, Card, Flex, List } from 'antd'
import {
  PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'

import { KanbanColumn } from './KanbanColumn'
import { KanbanSortable } from './KanbanSortable'
import { useStyles } from './styles'
import {
  buildQueryKey,
  cardExistsInPages,
  COLUMN_DROPPABLE_PREFIX,
  dragSensorConfig,
  dropAnimationStyle,
  extractColumnId,
  findCardInQueryData,
  findStatusInPages,
  getNextPageParam,
  getQueryData,
  restoreCardToSource,
  SCROLL_THRESHOLD,
  statusQueryOptions,
  toStatusIdNumber,
  updateQueryDataAddCard,
  updateQueryDataRemoveCard
} from './utils'

import type {
  CardWithId,
  InfiniteQueryOptionsFn,
  MutationOptionsFn,
  OnDragEndWithCreatesOpportunity,
  OnDragEndWithRefusalReason,
  OnDragEndWithRequiresContract,
  OnDragEndWithRequiresRefusalReason,
  PaginatedPage,
  PaginatedResponse,
  PipelineQueryOptions,
  QueryKeyFn,
  StatusQueryOptions,
  StatusWithRefusalReason,
  UpdateMutationBody,
  UpdateMutationResponse
} from '@shared/types'

type Props<
  TCard extends CardWithId,
  TStatusQueryOptions extends StatusQueryOptions,
  TPipelineQueryOptions extends PipelineQueryOptions,
  TUpdateBody extends UpdateMutationBody
> = PropsWithChildren & {
  header?: React.ReactNode
  pipelineListQueryOptions: TPipelineQueryOptions
  extraStatusQueryOptions?: TStatusQueryOptions
  cardRender: (data: TCard) => React.ReactNode
  statusesQuery: InfiniteQueryOptionsFn<TStatusQueryOptions>
  statusesQueryKey: QueryKeyFn<TStatusQueryOptions>
  pipelineListQuery: InfiniteQueryOptionsFn<TPipelineQueryOptions>
  pipelineListUpdateMutation: MutationOptionsFn<TUpdateBody>
  pipelineListQueryKey: QueryKeyFn<TPipelineQueryOptions>
  onDragEndWithRefusalReason?: (
    params: OnDragEndWithRefusalReason<TUpdateBody>
  ) => void
  onDragEndWithCreatesOpportunity?: (
    params: OnDragEndWithCreatesOpportunity<TUpdateBody>
  ) => void
  onDragEndWithRequiresRefusalReason?: (
    params: OnDragEndWithRequiresRefusalReason<TUpdateBody>
  ) => void
  onDragEndWithRequiresContract?: (
    params: OnDragEndWithRequiresContract<TUpdateBody>
  ) => void
}

export const Kanban = <
  TCard extends CardWithId,
  TStatusQueryOptions extends StatusQueryOptions,
  TPipelineQueryOptions extends PipelineQueryOptions,
  TUpdateBody extends UpdateMutationBody
>(
  props: Props<TCard, TStatusQueryOptions, TPipelineQueryOptions, TUpdateBody>
) => {
  const {
    header,
    pipelineListQueryOptions,
    extraStatusQueryOptions,
    cardRender,
    statusesQuery,
    statusesQueryKey,
    pipelineListQuery,
    pipelineListUpdateMutation,
    pipelineListQueryKey,
    onDragEndWithRefusalReason,
    onDragEndWithCreatesOpportunity,
    onDragEndWithRequiresRefusalReason,
    onDragEndWithRequiresContract,
    children
  } = props
  const { styles } = useStyles()
  const { message } = App.useApp()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const queryParams = useQueryParams()

  const [activeKanbanId, setActiveKanbanId] = useState<string>()

  const originalSourceColumnRef = useRef<number | null>(null)
  const previousTargetColumnRef = useRef<number | null>(null)
  const activeKanbanRef = useRef<TCard | undefined>(undefined)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const statusQuery = useMemo(
    () =>
      ({
        ...statusQueryOptions,
        ...extraStatusQueryOptions
      } as TStatusQueryOptions),
    [extraStatusQueryOptions]
  )

  const { data, fetchNextPage, isFetching, hasNextPage, isLoading } =
    useInfiniteQuery<PaginatedPage<StatusWithRefusalReason>>({
      ...(statusesQuery({ query: statusQuery }) as ReturnType<
        typeof useInfiniteQuery
      >),
      queryKey: statusesQueryKey({ query: statusQuery }),
      getNextPageParam: (lastPage) =>
        getNextPageParam<StatusWithRefusalReason>(lastPage),
      initialPageParam: 1
    })

  const kanbanAllStatuses = useMemo(
    () =>
      data?.pages?.flatMap(
        (page) => (page as PaginatedResponse<StatusWithRefusalReason>)?.results
      ),
    [data]
  )

  const disabledColumnIds = useMemo(() => {
    const sourceColumnId = originalSourceColumnRef.current
    if (!activeKanbanId || !sourceColumnId) return []

    const sourceStatus = kanbanAllStatuses?.find(
      (s) => Number(s.id) === sourceColumnId
    )

    const cardStatus = activeKanbanRef.current?.status
    const cardAllowedIds =
      typeof cardStatus === 'object' && cardStatus !== null
        ? (
            cardStatus as {
              id: number | string
              allowed_transition_statuses?: number[]
            }
          ).allowed_transition_statuses
        : undefined

    const allowedIds =
      sourceStatus?.allowed_transition_statuses !== undefined
        ? sourceStatus.allowed_transition_statuses
        : cardAllowedIds

    if (allowedIds === undefined || allowedIds === null) return []

    return (kanbanAllStatuses ?? [])
      .filter(
        (s) =>
          !allowedIds.includes(Number(s.id)) && Number(s.id) !== sourceColumnId
      )
      .map((s) => Number(s.id))
  }, [activeKanbanId, kanbanAllStatuses])

  const { mutate } = useMutation({
    ...(pipelineListUpdateMutation() as UseMutationOptions<
      UpdateMutationResponse,
      unknown,
      { path: { id: number }; body?: TUpdateBody }
    >)
  })

  const collisionDetection = useCallback(
    (args: Parameters<typeof closestCorners>[0]) => {
      const containers = args.droppableContainers.filter((c) =>
        String(c.id).startsWith(COLUMN_DROPPABLE_PREFIX)
      )

      const containerCollisions = pointerWithin({
        ...args,
        droppableContainers: containers
      })
      if (containerCollisions.length > 0) return containerCollisions

      const closestContainerCollisions = closestCorners({
        ...args,
        droppableContainers: containers
      })
      if (closestContainerCollisions.length > 0)
        return closestContainerCollisions

      return closestCorners(args)
    },
    []
  )

  const sensors = useSensors(
    useSensor(PointerSensor, dragSensorConfig.pointer),
    useSensor(KeyboardSensor, dragSensorConfig.keyboard)
  )

  const measuring = useMemo(
    () => ({
      droppable: {
        strategy: MeasuringStrategy.Always
      }
    }),
    []
  )

  const onDragStart = ({ active }: DragStartEvent) => {
    const activeItemId = String(active?.id)
    const activeContainerId = active?.data?.current?.sortable?.containerId

    if (activeItemId) {
      setActiveKanbanId(activeItemId)
    }

    if (activeContainerId) {
      const sourceColumnId = Number(activeContainerId)
      originalSourceColumnRef.current = sourceColumnId

      const sourceQueryKey = buildQueryKey(
        pipelineListQueryKey,
        pipelineListQueryOptions,
        sourceColumnId
      )
      const sourceData = getQueryData<TCard>(queryClient, sourceQueryKey)
      const card = findCardInQueryData(sourceData, activeItemId)
      if (card) {
        activeKanbanRef.current = card
      }
    }

    previousTargetColumnRef.current = null
  }

  const clearDragState = useCallback(() => {
    setActiveKanbanId(undefined)
    activeKanbanRef.current = undefined
    previousTargetColumnRef.current = null
    originalSourceColumnRef.current = null
  }, [])

  const updateStatus = useCallback(
    (
      kanbanId: string,
      body: TUpdateBody,
      rollbackKeys: QueryKey[],
      sourceColumnId: number | null
    ) => {
      mutate(
        { path: { id: Number(kanbanId) }, body } as Parameters<
          typeof mutate
        >[0],
        {
          onSuccess: (data) => {
            message.success(t('success_data_save'))

            const newStatusId = toStatusIdNumber(data?.status?.id)

            const queriesToInvalidate: QueryKey[] = [
              buildQueryKey(
                pipelineListQueryKey,
                pipelineListQueryOptions,
                newStatusId
              )
            ]

            if (sourceColumnId && sourceColumnId !== newStatusId) {
              queriesToInvalidate.push(
                buildQueryKey(
                  pipelineListQueryKey,
                  pipelineListQueryOptions,
                  sourceColumnId
                )
              )
            }

            queriesToInvalidate.forEach((queryKey) => {
              queryClient.invalidateQueries({ queryKey })
            })
          },
          onError: (err) => {
            showApiErrors(err)
            rollbackKeys.forEach((queryKey) => {
              queryClient.invalidateQueries({ queryKey })
            })
          }
        }
      )
    },
    [
      mutate,
      queryClient,
      pipelineListQueryKey,
      pipelineListQueryOptions,
      message,
      t
    ]
  )

  const onDragOver = useCallback(
    ({ active, over }: DragOverEvent) => {
      if (!over) return

      const activeId = String(active?.id)
      const fromColumnId = originalSourceColumnRef.current
      const toColumnId = extractColumnId(over)

      if (!toColumnId || !fromColumnId) return

      if (disabledColumnIds.includes(toColumnId)) return

      const previousTargetColumnId = previousTargetColumnRef.current

      const fromQueryKey = buildQueryKey(
        pipelineListQueryKey,
        pipelineListQueryOptions,
        fromColumnId
      )
      const toQueryKey = buildQueryKey(
        pipelineListQueryKey,
        pipelineListQueryOptions,
        toColumnId
      )

      if (toColumnId === fromColumnId && !previousTargetColumnId) {
        return
      }

      if (toColumnId === fromColumnId && previousTargetColumnId) {
        if (previousTargetColumnId !== fromColumnId) {
          const previousTargetQueryKey = buildQueryKey(
            pipelineListQueryKey,
            pipelineListQueryOptions,
            previousTargetColumnId
          )
          updateQueryDataRemoveCard(
            queryClient,
            previousTargetQueryKey,
            activeId
          )
        }

        updateQueryDataAddCard(
          queryClient,
          fromQueryKey,
          activeKanbanRef.current,
          fromColumnId
        )
        previousTargetColumnRef.current = null
        return
      }

      const toColumnData = getQueryData<TCard>(queryClient, toQueryKey)
      if (
        toColumnData?.pages &&
        cardExistsInPages(toColumnData.pages, activeId)
      ) {
        return
      }

      previousTargetColumnRef.current = toColumnId

      if (
        previousTargetColumnId &&
        previousTargetColumnId !== toColumnId &&
        previousTargetColumnId !== fromColumnId
      ) {
        const previousTargetQueryKey = buildQueryKey(
          pipelineListQueryKey,
          pipelineListQueryOptions,
          previousTargetColumnId
        )
        updateQueryDataRemoveCard(queryClient, previousTargetQueryKey, activeId)
      }

      updateQueryDataRemoveCard(queryClient, fromQueryKey, activeId)
      updateQueryDataAddCard(
        queryClient,
        toQueryKey,
        activeKanbanRef.current,
        toColumnId
      )
    },
    [
      queryClient,
      pipelineListQueryKey,
      pipelineListQueryOptions,
      disabledColumnIds
    ]
  )

  const onDragEnd = useCallback(
    ({ active: _active, over }: DragEndEvent) => {
      const currentCard = activeKanbanRef.current
      const currentKanbanId = currentCard ? String(currentCard.id) : undefined
      const fromColumnId = originalSourceColumnRef.current
      const toColumnId = over ? extractColumnId(over) : null
      const previousTargetColumnId = previousTargetColumnRef.current

      previousTargetColumnRef.current = null

      const hasNoActiveCard = !currentKanbanId || !currentCard
      const hasNoValidColumns = !fromColumnId || !toColumnId
      const isSameColumn = fromColumnId === toColumnId
      const hasNoDropTarget = !over

      const shouldCancelDrag =
        hasNoActiveCard || hasNoValidColumns || isSameColumn || hasNoDropTarget

      if (shouldCancelDrag) {
        if (currentKanbanId && fromColumnId) {
          const fromQueryKey = buildQueryKey(
            pipelineListQueryKey,
            pipelineListQueryOptions,
            fromColumnId
          )

          if (
            previousTargetColumnId &&
            previousTargetColumnId !== fromColumnId
          ) {
            const intermediateQueryKey = buildQueryKey(
              pipelineListQueryKey,
              pipelineListQueryOptions,
              previousTargetColumnId
            )
            updateQueryDataRemoveCard(
              queryClient,
              intermediateQueryKey,
              currentKanbanId
            )
          }

          if (toColumnId && toColumnId !== fromColumnId) {
            const toQueryKey = buildQueryKey(
              pipelineListQueryKey,
              pipelineListQueryOptions,
              toColumnId
            )
            updateQueryDataRemoveCard(queryClient, toQueryKey, currentKanbanId)
          }

          restoreCardToSource(
            queryClient,
            fromQueryKey,
            currentCard,
            currentKanbanId,
            fromColumnId
          )
        }

        clearDragState()
        return
      }

      const fromQueryKey = buildQueryKey(
        pipelineListQueryKey,
        pipelineListQueryOptions,
        fromColumnId
      )
      const toQueryKey = buildQueryKey(
        pipelineListQueryKey,
        pipelineListQueryOptions,
        toColumnId
      )

      if (disabledColumnIds.includes(toColumnId)) {
        updateQueryDataRemoveCard(queryClient, toQueryKey, currentKanbanId!)

        if (previousTargetColumnId && previousTargetColumnId !== fromColumnId) {
          const intermediateQueryKey = buildQueryKey(
            pipelineListQueryKey,
            pipelineListQueryOptions,
            previousTargetColumnId
          )
          updateQueryDataRemoveCard(
            queryClient,
            intermediateQueryKey,
            currentKanbanId!
          )
        }

        restoreCardToSource(
          queryClient,
          fromQueryKey,
          currentCard,
          currentKanbanId!,
          fromColumnId
        )
        clearDragState()
        message.error(t('transition_not_allowed'))
        return
      }

      const statusData = findStatusInPages<StatusWithRefusalReason>(
        data?.pages as PaginatedPage<StatusWithRefusalReason>[],
        toColumnId
      )

      const baseBody = { status: toColumnId }

      const rollback = () => {
        updateQueryDataRemoveCard(queryClient, toQueryKey, currentKanbanId!)
        restoreCardToSource(
          queryClient,
          fromQueryKey,
          currentCard,
          currentKanbanId!,
          fromColumnId
        )
      }

      const wrappedUpdateStatus = (body: TUpdateBody) =>
        updateStatus(
          currentKanbanId!,
          body,
          [fromQueryKey, toQueryKey],
          fromColumnId
        )

      clearDragState()

      if (!statusData) {
        wrappedUpdateStatus(baseBody as TUpdateBody)
        return
      }

      if (statusData?.requires_opportunity && onDragEndWithCreatesOpportunity) {
        onDragEndWithCreatesOpportunity({
          kanbanId: currentKanbanId!,
          statusId: toColumnId,
          updateStatus: (body) => wrappedUpdateStatus(body),
          rollback,
          fromQueryKey,
          toQueryKey
        })
        return
      }

      if (
        statusData?.requires_refusal_reason &&
        onDragEndWithRequiresRefusalReason
      ) {
        onDragEndWithRequiresRefusalReason({
          kanbanId: currentKanbanId!,
          statusId: toColumnId,
          updateStatus: (body) => wrappedUpdateStatus(body),
          rollback,
          fromQueryKey,
          toQueryKey
        })
        return
      }

      if (statusData?.requires_contract && onDragEndWithRequiresContract) {
        onDragEndWithRequiresContract({
          kanbanId: currentKanbanId!,
          statusId: toColumnId,
          updateStatus: (body) => wrappedUpdateStatus(body),
          rollback,
          fromQueryKey,
          toQueryKey
        })
        return
      }

      const firstReason = statusData?.refusal_reason?.[0]

      if (!firstReason) {
        wrappedUpdateStatus(baseBody as TUpdateBody)
        return
      }

      if (onDragEndWithRefusalReason) {
        onDragEndWithRefusalReason({
          kanbanId: currentKanbanId!,
          statusId: toColumnId,
          reasonId: firstReason.id,
          emailTemplate: firstReason.email_template,
          emailOnRejection: firstReason.email_on_rejection,
          updateStatus: (customBody) => wrappedUpdateStatus(customBody),
          fromQueryKey,
          toQueryKey
        })
        return
      }

      const bodyWithReason = { ...baseBody, reason: firstReason.id }

      wrappedUpdateStatus(bodyWithReason as unknown as TUpdateBody)
    },
    [
      pipelineListQueryOptions,
      data,
      updateStatus,
      clearDragState,
      queryClient,
      pipelineListQueryKey,
      onDragEndWithRefusalReason,
      onDragEndWithCreatesOpportunity,
      onDragEndWithRequiresRefusalReason,
      onDragEndWithRequiresContract,
      disabledColumnIds,
      message,
      t
    ]
  )

  const onDragCancel = useCallback(() => {
    const currentCard = activeKanbanRef.current
    const currentKanbanId = currentCard ? String(currentCard.id) : undefined
    const fromColumnId = originalSourceColumnRef.current
    const previousTargetColumnId = previousTargetColumnRef.current

    if (currentKanbanId && fromColumnId) {
      const fromQueryKey = buildQueryKey(
        pipelineListQueryKey,
        pipelineListQueryOptions,
        fromColumnId
      )

      if (previousTargetColumnId && previousTargetColumnId !== fromColumnId) {
        const intermediateQueryKey = buildQueryKey(
          pipelineListQueryKey,
          pipelineListQueryOptions,
          previousTargetColumnId
        )
        updateQueryDataRemoveCard(
          queryClient,
          intermediateQueryKey,
          currentKanbanId
        )
      }

      restoreCardToSource(
        queryClient,
        fromQueryKey,
        currentCard,
        currentKanbanId,
        fromColumnId
      )
    }

    clearDragState()
  }, [
    queryClient,
    pipelineListQueryKey,
    pipelineListQueryOptions,
    clearDragState
  ])

  const onScroll = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container || !hasNextPage || isFetching) return

    const { scrollLeft, scrollWidth, clientWidth } = container
    if (scrollLeft + clientWidth >= scrollWidth - SCROLL_THRESHOLD) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetching, fetchNextPage])

  const memoizedFilteredData = useMemo(
    () =>
      queryParams?.status
        ? kanbanAllStatuses?.filter(
            (status) => Number(queryParams?.status) === status?.id
          )
        : kanbanAllStatuses,
    [kanbanAllStatuses, queryParams?.status]
  )

  const kanbanColumns = useMemo(
    () =>
      memoizedFilteredData?.map((statusData) => ({
        id: statusData.id,
        data: statusData
      })) || [],
    [memoizedFilteredData]
  )

  const renderColumn = useCallback(
    (column: { id: string | number; data: StatusWithRefusalReason }) => (
      <KanbanColumn<TCard, StatusWithRefusalReason, TPipelineQueryOptions>
        key={column.id}
        columnData={column.data}
        cardRender={cardRender}
        pipelineListQuery={pipelineListQuery}
        pipelineListQueryKey={pipelineListQueryKey}
        pipelineListQueryOptions={pipelineListQueryOptions}
        isDisabled={disabledColumnIds.includes(Number(column.id))}
      />
    ),
    [
      cardRender,
      pipelineListQuery,
      pipelineListQueryKey,
      pipelineListQueryOptions,
      disabledColumnIds
    ]
  )

  return (
    <>
      <Card>
        <Flex vertical gap={12}>
          {header}

          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragEnd={onDragEnd}
            onDragCancel={onDragCancel}
            measuring={measuring}
          >
            {data?.pages?.[0]?.count ? (
              <SkeletonWrapper loading={isLoading} paragraph={{ rows: 22 }}>
                <div
                  ref={scrollContainerRef}
                  onScroll={onScroll}
                  className={styles.scrollContainer}
                >
                  <List
                    className={styles.list}
                    dataSource={kanbanColumns}
                    renderItem={renderColumn}
                  />
                </div>
              </SkeletonWrapper>
            ) : null}

            <DragOverlay
              dropAnimation={dropAnimationStyle}
              className={styles.draggable}
            >
              {activeKanbanId && activeKanbanRef.current && (
                <KanbanSortable id={String(activeKanbanRef.current.id)}>
                  {cardRender?.(activeKanbanRef.current)}
                </KanbanSortable>
              )}
            </DragOverlay>
          </DndContext>
        </Flex>
      </Card>

      {children}
    </>
  )
}
